import React, { useState, useEffect } from "react";
import { Sun, CloudSun, Moon, Settings } from "lucide-react";
import { Button } from "./ui/button";
import { useI18n } from "../i18n";
import type { GlobalIllumination } from "@roll1/shared";

interface MapSettingsPopoverProps {
  gridSize: number;
  mapScale: number;
  bgColor: string;
  mapUrl: string | null;
  globalIllumination?: GlobalIllumination;
  onGridSizeChange: (size: number) => void;
  onMapScaleChange: (scale: number) => void;
  onBgColorChange: (color: string) => void;
  onDeleteMap: () => void;
  onGlobalIlluminationChange?: (gi: GlobalIllumination) => void;
}

const MapSettingsPopover = React.memo(function MapSettingsPopover({
  gridSize,
  mapScale,
  bgColor,
  mapUrl,
  globalIllumination,
  onGridSizeChange,
  onMapScaleChange,
  onBgColorChange,
  onDeleteMap,
  onGlobalIlluminationChange,
}: MapSettingsPopoverProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [localGridSize, setLocalGridSize] = useState(gridSize);
  const [localMapScale, setLocalMapScale] = useState(mapScale);
  const [localBgColor, setLocalBgColor] = useState(bgColor);

  // Sync local state when popover opens (re-read from refs)
  useEffect(() => {
    if (open) {
      setLocalGridSize(gridSize);
      setLocalMapScale(mapScale);
      setLocalBgColor(bgColor);
    }
  }, [open, gridSize, mapScale, bgColor]);

  return (
    <div className="relative inline-block" data-testid="map-settings">
      <button
        onClick={() => {
          setOpen((prev) => !prev);
          setConfirmDelete(false);
        }}
        title={t("toolOptions.mapSettings")}
        data-testid="map-settings-toggle"
      >
        <Settings size={14} />
      </button>
      {open && (
        <div
          className="absolute bottom-full left-0 z-[var(--z-overlay)] mb-1 rounded-md border border-zinc-700 bg-zinc-900 p-3 shadow-lg"
          style={{ minWidth: 220 }}
          data-testid="map-settings-panel"
        >
          <div className="space-y-3">
            <label className="block text-xs text-zinc-300">
              {t("toolOptions.gridSize")} {localGridSize}px
              <input
                type="range"
                min={20}
                max={200}
                step={10}
                value={localGridSize}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setLocalGridSize(val);
                  onGridSizeChange(val);
                }}
                className="mt-1 block w-full"
                data-testid="grid-size-slider"
              />
            </label>
            <label className="block text-xs text-zinc-300">
              {t("toolOptions.mapScale")} {localMapScale.toFixed(1)}x
              <input
                type="range"
                min={0.1}
                max={5.0}
                step={0.1}
                value={localMapScale}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setLocalMapScale(val);
                  onMapScaleChange(val);
                }}
                className="mt-1 block w-full"
                data-testid="map-scale-slider"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              {t("toolOptions.bgColor")}
              <input
                type="color"
                value={localBgColor || "#1a1f2b"}
                onChange={(e) => {
                  setLocalBgColor(e.target.value);
                  onBgColorChange(e.target.value);
                }}
                className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                data-testid="bg-color-picker"
              />
              {localBgColor && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1 text-[10px]"
                  onClick={() => {
                    setLocalBgColor("");
                    onBgColorChange("");
                  }}
                >
                  ✕
                </Button>
              )}
            </label>
            {onGlobalIlluminationChange && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-300">{t("page.globalIllumination")}</span>
                  <input
                    type="color"
                    value={globalIllumination?.color || "#ffffff"}
                    onChange={(e) => {
                      onGlobalIlluminationChange({
                        level: globalIllumination?.level ?? 'bright',
                        color: e.target.value,
                      });
                    }}
                    className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent p-0"
                    title={t("page.illuminationColor")}
                    data-testid="gi-color-picker"
                  />
                  {globalIllumination?.color && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-1 text-[10px]"
                      onClick={() => {
                        onGlobalIlluminationChange({
                          level: globalIllumination?.level ?? 'bright',
                          color: undefined,
                        });
                      }}
                    >
                      ✕
                    </Button>
                  )}
                </div>
                <div className="flex gap-1">
                  {([
                    { level: 'bright' as const, icon: Sun, label: t("page.illuminationBright") },
                    { level: 'dim' as const, icon: CloudSun, label: t("page.illuminationDim") },
                    { level: 'dark' as const, icon: Moon, label: t("page.illuminationDark") },
                  ] as const).map(({ level, icon: Icon, label }) => {
                    const active = globalIllumination?.level === level;
                    return (
                      <Button
                        key={level}
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        style={active ? { background: 'var(--color-primary)', color: 'var(--color-primary-foreground)' } : undefined}
                        onClick={() => onGlobalIlluminationChange({
                          level,
                          color: globalIllumination?.color,
                        })}
                        title={label}
                      >
                        <Icon size={14} />
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
            {mapUrl && (
              <div>
                {confirmDelete ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-400">
                      {t("toolOptions.confirmDeleteMap")}
                    </span>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => {
                        onDeleteMap();
                        setConfirmDelete(false);
                        setOpen(false);
                      }}
                      data-testid="confirm-delete-map"
                    >
                      {"OK"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => setConfirmDelete(false)}
                    >
                      {"X"}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-red-400"
                    onClick={() => setConfirmDelete(true)}
                    data-testid="delete-map-btn"
                  >
                    {t("toolOptions.deleteMap")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default MapSettingsPopover;
